import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { z } from 'zod';
import { Button, Card, Dialog, Field } from '../../ui';
import {
  adoptCatalogueExercise,
  createEntry,
  createSession,
  deleteEntry,
  deleteSession,
  listCatalogue,
  listEntries,
  listLibraryItems,
  listRecentEntries,
  listSessions,
  swapEntryPositions,
  updateLibraryItem,
} from '../../shared/queries';
import type { ExerciseEntryRow, SessionRow, WeekRow } from '../../shared/queries';
import { supabase } from '../../shared/supabase/client';
import { formatExerciseDetail } from '../../shared/exerciseLine';
import { weekdayNames } from '../../shared/formatDate';
import { buildPickerOptions, recentOptions } from '../../shared/exercisePicker';
import type { PickerOption } from '../../shared/exercisePicker';
import { ExercisePicker } from './ExercisePicker';
import { publishedWeekNoticeId } from './publishedWeekNotice';

/**
 * The sessions inside one week, and the exercises inside one session.
 *
 * Entries are ad hoc: a name and whatever of sets, reps, load, rest and a note
 * the coach wants to give. Rest is a whole number of seconds; everything else
 * the coach types is kept in her own words. There is no exercise library behind them yet, and
 * `library_item_id` already exists on the row so that connecting them to one
 * later is an `UPDATE` rather than a migration (design.md F3).
 *
 * `coach_note` is deliberately **not** encrypted. It is the coach's own
 * training instruction — "heels down, sit back" — rather than the client's
 * words about themselves, and D12 rule 6 does not list it. It also has to stay
 * plainly queryable.
 */
export function SessionEditor({
  week,
  clientId,
  coachId,
}: {
  week: WeekRow;
  clientId: string;
  coachId: string;
}) {
  const sessions = useQuery({
    queryKey: ['sessions', week.id],
    queryFn: () => listSessions(supabase, week.id),
  });

  const [openSessionId, setOpenSessionId] = useState<string | null>(null);

  return (
    <div className="flex flex-col gap-4">
      <NewSessionForm
        week={week}
        clientId={clientId}
        coachId={coachId}
        onCreated={setOpenSessionId}
      />

      {sessions.data?.length === 0 ? (
        <p className="text-ink-muted text-sm">No sessions in this week yet.</p>
      ) : null}

      <ul className="flex flex-col gap-3">
        {(sessions.data ?? []).map((session) => (
          <li key={session.id}>
            <SessionCard
              session={session}
              week={week}
              clientId={clientId}
              coachId={coachId}
              open={openSessionId === session.id}
              onToggle={() => {
                setOpenSessionId(openSessionId === session.id ? null : session.id);
              }}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

const sessionSchema = z.object({
  title: z.string().trim().min(1, 'Give the session a name.').max(120, 'That is too long.'),
  dayOfWeek: z.coerce.number().int().min(1).max(7),
  coachNote: z.string().trim().max(2000, 'That is too long.'),
});

function NewSessionForm({
  week,
  clientId,
  coachId,
  onCreated,
}: {
  week: WeekRow;
  clientId: string;
  coachId: string;
  onCreated: (id: string) => void;
}) {
  const queryClient = useQueryClient();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<z.input<typeof sessionSchema>, unknown, z.output<typeof sessionSchema>>({
    resolver: zodResolver(sessionSchema),
    defaultValues: { title: '', dayOfWeek: 1, coachNote: '' },
  });

  const add = useMutation({
    mutationFn: (values: z.output<typeof sessionSchema>) =>
      createSession(supabase, {
        coach_id: coachId,
        client_id: clientId,
        week_id: week.id,
        title: values.title,
        day_of_week: values.dayOfWeek,
        coach_note: values.coachNote === '' ? null : values.coachNote,
        position: 0,
      }),
    onSuccess: async (session) => {
      reset();
      onCreated(session.id);
      await queryClient.invalidateQueries({ queryKey: ['sessions', week.id] });
    },
  });

  return (
    <Card className="bg-surface">
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(event) =>
          void handleSubmit((values) => {
            add.mutate(values);
          })(event)
        }
      >
        <Field
          label="Session"
          className="min-w-48 flex-1"
          error={errors.title?.message}
          {...register('title')}
        />

        <div className="flex flex-col gap-1.5">
          <label htmlFor={`day-${week.id}`} className="text-ink text-sm font-medium">
            Day
          </label>
          <select
            id={`day-${week.id}`}
            className="bg-surface-raised text-ink border-border-strong min-h-11 rounded-[--radius-control] border px-3 text-base"
            {...register('dayOfWeek')}
          >
            {/* `day_of_week` is 1-7 Monday-first in `00002`/`00003`, so the
                name at index n-1 is the day numbered n. */}
            {weekdayNames().map((name, index) => (
              <option key={name} value={index + 1}>
                {name}
              </option>
            ))}
          </select>
        </div>

        <Field
          label="Note (optional)"
          className="min-w-48 flex-1"
          error={errors.coachNote?.message}
          {...register('coachNote')}
        />

        <Button type="submit" disabled={add.isPending}>
          Add session
        </Button>
      </form>

      {add.isError ? (
        <p role="alert" className="text-danger mt-3 text-sm">
          {add.error.message}
        </p>
      ) : null}
    </Card>
  );
}

function SessionCard({
  session,
  week,
  clientId,
  coachId,
  open,
  onToggle,
}: {
  session: SessionRow;
  week: WeekRow;
  clientId: string;
  coachId: string;
  open: boolean;
  onToggle: () => void;
}) {
  const dayLabel = weekdayNames()[session.day_of_week - 1] ?? '';
  const [confirming, setConfirming] = useState(false);

  /*
   * The rule is the database's (`00015_delete_gate.sql`), not this component's.
   * What happens here is only that a refusal is normally never reached: the
   * control is disabled while the week is published, so the coach learns the
   * rule from a control she can see rather than from an error. `SessionCard`
   * already receives the whole `week` row, so `week.status` costs no plumbing.
   *
   * Disabled, not absent — design D6. A control that vanishes teaches nothing.
   */
  const published = week.status === 'published';

  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button type="button" onClick={onToggle} className="text-left">
          <span className="text-ink font-medium">{session.title}</span>
          <span className="text-ink-muted ml-2 text-xs">{dayLabel}</span>
        </button>
        <div className="flex items-center gap-1">
          <Button variant="ghost" onClick={onToggle}>
            {open ? 'Hide exercises' : 'Exercises'}
          </Button>
          <Button
            variant="danger-ghost"
            aria-label={`Delete ${session.title}`}
            disabled={published}
            aria-describedby={published ? publishedWeekNoticeId(week.id) : undefined}
            onClick={() => {
              setConfirming(true);
            }}
          >
            Delete
          </Button>
        </div>
      </div>

      {confirming ? (
        <DeleteSessionDialog
          session={session}
          week={week}
          onClose={() => {
            setConfirming(false);
          }}
        />
      ) : null}

      {open ? (
        <div className="border-border mt-4 border-t pt-4">
          <EntryEditor session={session} week={week} clientId={clientId} coachId={coachId} />
        </div>
      ) : null}
    </Card>
  );
}

/**
 * Deleting a session keeps a confirmation, where removing an exercise does not.
 *
 * The unit is larger and the gesture is much rarer — and the question can state
 * what goes with it, which is information she does not otherwise have in front
 * of her: the exercises are inside a collapsed card. An undo would have to hold
 * a session and every entry under it and re-insert the tree; a dialog is honest
 * about a delete that is genuinely final (design D9).
 *
 * Modelled on `TemplatesPage`'s `DeleteDialog`, including its asymmetric labels:
 * "Keep it" is a sentence about the session, where "Cancel" is a sentence about
 * the dialog.
 */
function DeleteSessionDialog({
  session,
  week,
  onClose,
}: {
  session: SessionRow;
  week: WeekRow;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();

  // The same query key `EntryEditor` uses, so an open session costs no request.
  const entries = useQuery({
    queryKey: ['entries', session.id],
    queryFn: () => listEntries(supabase, session.id),
  });

  const remove = useMutation({
    mutationFn: () => deleteSession(supabase, session.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['sessions', week.id] });
      onClose();
    },
  });

  const count = entries.data?.length;

  return (
    <Dialog open onClose={onClose} title="Delete this session?">
      <p className="text-ink-soft mt-3 text-sm leading-relaxed">
        “{session.title}”{' '}
        {count === undefined
          ? 'and every exercise in it will be removed.'
          : count === 0
            ? 'will be removed. It has no exercises in it.'
            : `and its ${String(count)} ${count === 1 ? 'exercise' : 'exercises'} will be removed.`}{' '}
        This cannot be undone.
      </p>

      {remove.isError ? (
        <p role="alert" className="text-danger mt-4 text-sm">
          {remove.error.message}
        </p>
      ) : null}

      <div className="mt-5 flex flex-wrap justify-end gap-2">
        <Button variant="ghost" onClick={onClose}>
          Keep it
        </Button>
        <Button
          variant="danger"
          disabled={remove.isPending}
          onClick={() => {
            remove.mutate();
          }}
        >
          {remove.isPending ? 'Deleting…' : 'Delete session'}
        </Button>
      </div>
    </Dialog>
  );
}

/**
 * Rest is the one field the coach has no voice in — nobody's coaching identity
 * is expressed in whether they wrote "90s" or "90 sec" — so it is the one field
 * the product normalises: whole seconds, nothing else (design.md E1 and E2).
 * Reps and load are her own words and are deliberately left unconstrained.
 */
const entrySchema = z.object({
  name: z.string().trim().min(1, 'Name the exercise.').max(120, 'That is too long.'),
  sets: z.string().trim(),
  reps: z.string().trim().max(40, 'That is too long.'),
  load: z.string().trim().max(40, 'That is too long.'),
  restSeconds: z
    .string()
    .trim()
    .refine((value) => value === '' || /^\d+$/.test(value), 'Whole seconds, as a number.')
    .refine((value) => value === '' || Number(value) <= 3600, 'An hour is the longest rest.'),
  note: z.string().trim().max(1000, 'That is too long.'),
});

/** The rests a coach actually prescribes, one tap each (design.md E4). */
const REST_CHIPS = [60, 90, 120, 180];

function EntryEditor({
  session,
  week,
  clientId,
  coachId,
}: {
  session: SessionRow;
  week: WeekRow;
  clientId: string;
  coachId: string;
}) {
  const queryClient = useQueryClient();

  /** Removal is refused on a published week; adding and editing are not. */
  const published = week.status === 'published';

  const entries = useQuery({
    queryKey: ['entries', session.id],
    queryFn: () => listEntries(supabase, session.id),
  });

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    control,
    formState: { errors },
  } = useForm<z.infer<typeof entrySchema>>({
    resolver: zodResolver(entrySchema),
    defaultValues: { name: '', sets: '', reps: '', load: '', restSeconds: '', note: '' },
  });

  /*
   * What the picker offers. Three reads, all cached by key, so the catalogue is
   * fetched once for the whole editor rather than once per open session.
   *
   * The catalogue query asks for no tenant filter and needs none: it is
   * reference data with a `using (true)` policy. Her library and her recent
   * entries are fenced by row-level security exactly like everything else here.
   */
  const catalogue = useQuery({
    queryKey: ['exercise-catalogue'],
    queryFn: () => listCatalogue(supabase),
    staleTime: Infinity,
  });
  const library = useQuery({
    queryKey: ['exercise-library'],
    queryFn: () => listLibraryItems(supabase),
  });
  const recent = useQuery({
    queryKey: ['recent-entries'],
    queryFn: () => listRecentEntries(supabase),
  });

  const options = buildPickerOptions(library.data ?? [], catalogue.data ?? []);
  const recents = recentOptions(recent.data ?? [], options);

  /*
   * `useWatch` rather than `watch()`: the picker is a controlled field, and
   * `watch()` returns a function the React compiler refuses to memoize, which
   * is a lint failure here rather than a style preference.
   */
  const typedName = useWatch({ control, name: 'name' });

  /** The row she chose, if she chose one rather than typing her own words. */
  const [picked, setPicked] = useState<PickerOption | null>(null);
  const [editing, setEditing] = useState<PickerOption | null>(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['entries', session.id] });

  const add = useMutation({
    mutationFn: async (values: z.infer<typeof entrySchema>) => {
      const sets = values.sets === '' ? null : Number.parseInt(values.sets, 10);

      /*
       * Adoption happens here, at the moment the exercise is actually used,
       * rather than when she clicks a row in the list. Adopting on a click
       * would leave a library item behind for every exercise she looked at and
       * changed her mind about.
       *
       * Her entry then points at **her** item, never at the catalogue — which
       * is what makes a published plan survive the catalogue being removed
       * (design C8).
       */
      let libraryItemId = picked?.libraryItemId ?? null;
      if (libraryItemId === null && picked?.catalogId != null && picked.name === values.name) {
        const adopted = await adoptCatalogueExercise(supabase, {
          coachId,
          catalogId: picked.catalogId,
          name: picked.name,
        });
        libraryItemId = adopted.id;
        await queryClient.invalidateQueries({ queryKey: ['exercise-library'] });
      }

      return createEntry(supabase, {
        coach_id: coachId,
        client_id: clientId,
        session_id: session.id,
        week_id: week.id,
        name: values.name,
        library_item_id: picked?.name === values.name ? libraryItemId : null,
        sets: sets === null || Number.isNaN(sets) ? null : sets,
        reps: values.reps === '' ? null : values.reps,
        load: values.load === '' ? null : values.load,
        rest_seconds: values.restSeconds === '' ? null : Number(values.restSeconds),
        note: values.note === '' ? null : values.note,
        position: entries.data?.length ?? 0,
      });
    },
    onSuccess: async () => {
      reset();
      setPicked(null);
      await Promise.all([
        invalidate(),
        queryClient.invalidateQueries({ queryKey: ['recent-entries'] }),
      ]);
    },
  });

  const move = useMutation({
    mutationFn: async ({ index, delta }: { index: number; delta: -1 | 1 }) => {
      const list = entries.data ?? [];
      const a = list[index];
      const b = list[index + delta];
      if (!a || !b) return;
      await swapEntryPositions(
        supabase,
        { id: a.id, position: a.position },
        { id: b.id, position: b.position },
      );
    },
    onSuccess: invalidate,
  });

  /**
   * Removing an exercise is undoable rather than confirmed.
   *
   * She is assembling an eight-exercise session: she would confirm eight times
   * to build it and twice more to fix it, and a dialog dismissed forty times a
   * week is a dialog she will dismiss on the one that mattered. The gate
   * sharpens the argument rather than weakening it — with removal refused
   * entirely on a published week, the only removal that can happen is from a
   * draft nobody has ever seen, where a mistake costs a retype (design D9).
   *
   * The removed row is held here in full, so the undo restores what she typed
   * rather than an exercise with the same name. Not a timed toast: she is at a
   * desk and gets interrupted, so the offer stands for the rest of the editing
   * session.
   */
  const [removed, setRemoved] = useState<ExerciseEntryRow | null>(null);

  const remove = useMutation({
    mutationFn: async (entry: ExerciseEntryRow) => {
      await deleteEntry(supabase, entry.id);
      return entry;
    },
    onSuccess: async (entry) => {
      setRemoved(entry);
      await invalidate();
    },
  });

  const restore = useMutation({
    mutationFn: (entry: ExerciseEntryRow) =>
      createEntry(supabase, {
        coach_id: entry.coach_id,
        client_id: entry.client_id,
        session_id: entry.session_id,
        week_id: entry.week_id,
        name: entry.name,
        library_item_id: entry.library_item_id,
        sets: entry.sets,
        reps: entry.reps,
        load: entry.load,
        rest_seconds: entry.rest_seconds,
        note: entry.note,
        position: entry.position,
      }),
    onSuccess: async () => {
      setRemoved(null);
      await invalidate();
    },
  });

  const list = entries.data ?? [];

  return (
    <div className="flex flex-col gap-4">
      <ol className="flex flex-col gap-2">
        {list.map((entry, index) => (
          <li key={entry.id} className="flex flex-wrap items-center justify-between gap-2">
            <EntryLine entry={entry} />
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                aria-label={`Move ${entry.name} up`}
                disabled={index === 0 || move.isPending}
                onClick={() => {
                  move.mutate({ index, delta: -1 });
                }}
              >
                ↑
              </Button>
              <Button
                variant="ghost"
                aria-label={`Move ${entry.name} down`}
                disabled={index === list.length - 1 || move.isPending}
                onClick={() => {
                  move.mutate({ index, delta: 1 });
                }}
              >
                ↓
              </Button>
              {/*
                Red at rest, not teal. This control read `text-primary` — the
                same colour as "Move up", "Exercises" and "Publish week" — so
                the destructive control in the row was dressed as the
                constructive ones (design D8). Disabled rather than hidden on a
                published week, pointing at the week's one sentence, so the leaf
                and the branch agree (design D4 and D6).
              */}
              <Button
                variant="danger-ghost"
                aria-label={`Remove ${entry.name}`}
                disabled={published || remove.isPending}
                aria-describedby={published ? publishedWeekNoticeId(week.id) : undefined}
                onClick={() => {
                  remove.mutate(entry);
                }}
              >
                {remove.isPending && remove.variables.id === entry.id ? 'Removing…' : 'Remove'}
              </Button>
            </div>
          </li>
        ))}
      </ol>

      {list.length === 0 ? <p className="text-ink-muted text-sm">No exercises yet.</p> : null}

      {removed === null ? null : (
        <div className="flex flex-wrap items-center gap-2">
          <p role="status" className="text-ink-soft text-sm">
            Removed “{removed.name}”.
          </p>
          <Button
            variant="ghost"
            disabled={restore.isPending}
            onClick={() => {
              restore.mutate(removed);
            }}
          >
            {restore.isPending ? 'Putting it back…' : 'Undo'}
          </Button>
        </div>
      )}

      {remove.isError ? (
        <p role="alert" className="text-danger text-sm">
          {remove.error.message}
        </p>
      ) : null}

      {restore.isError ? (
        <p role="alert" className="text-danger text-sm">
          {restore.error.message}
        </p>
      ) : null}

      <form
        className="flex flex-col gap-2"
        onSubmit={(event) =>
          void handleSubmit((values) => {
            add.mutate(values);
          })(event)
        }
      >
        <div className="flex flex-wrap items-end gap-3">
          <ExercisePicker
            className="min-w-40 flex-1"
            value={typedName}
            onChange={(next) => {
              setValue('name', next, { shouldValidate: false, shouldDirty: true });
            }}
            onPick={setPicked}
            onEdit={setEditing}
            options={options}
            recents={recents}
            error={errors.name?.message}
          />
          <Field
            label="Sets"
            className="w-20"
            inputMode="numeric"
            placeholder="3"
            {...register('sets')}
          />
          {/*
            "Reps" and "Load" were the labels, and the free text behind them was
            invisible because of it. The fields are named for everything they
            take instead, which is what makes "30 s hold" and "RPE 7" look like
            use rather than misuse (design.md E3).
          */}
          <Field
            label="Reps or time"
            className="w-28"
            placeholder="8-10"
            error={errors.reps?.message}
            {...register('reps')}
          />
          <Field
            label="Load or effort"
            className="w-32"
            placeholder="16 kg"
            error={errors.load?.message}
            {...register('load')}
          />
          <Field
            label="Rest"
            className="w-28"
            type="number"
            inputMode="numeric"
            min={0}
            max={3600}
            step={1}
            placeholder="90"
            suffix="sec"
            error={errors.restSeconds?.message}
            {...register('restSeconds')}
          />

          <div className="flex flex-col gap-1.5">
            <span id={`rests-${session.id}`} className="text-ink text-sm font-medium">
              Common rests
            </span>
            <div
              role="group"
              aria-labelledby={`rests-${session.id}`}
              className="flex min-h-11 items-center gap-1"
            >
              {REST_CHIPS.map((seconds) => (
                <Button
                  key={seconds}
                  type="button"
                  variant="secondary"
                  className="px-2"
                  aria-label={`Rest ${String(seconds)} seconds`}
                  onClick={() => {
                    setValue('restSeconds', String(seconds), {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                  }}
                >
                  {seconds}
                </Button>
              ))}
            </div>
          </div>

          <Field
            label="Note"
            className="min-w-40 flex-1"
            placeholder="Sit back, heels down."
            error={errors.note?.message}
            {...register('note')}
          />
          <Button type="submit" disabled={add.isPending}>
            Add exercise
          </Button>
        </div>

        {/*
          One standing line under the whole row rather than a hint under each of
          six fields, which would double the row's height at the width the plan
          editor gives it (design.md E4). It teaches by example that reps and
          load take ordinary training language.
        */}
        <p className="text-ink-muted text-xs">
          Type it how you&rsquo;d say it: 8-10, 30 s hold, 400 m &middot; 16 kg, RPE 7, bodyweight.
        </p>
      </form>

      {editing === null ? null : (
        <MakeItYours
          option={editing}
          onDone={(renamed) => {
            setEditing(null);
            if (renamed !== null) {
              setValue('name', renamed, { shouldDirty: true });
              void queryClient.invalidateQueries({ queryKey: ['exercise-library'] });
            }
          }}
        />
      )}

      {add.isError ? (
        <p role="alert" className="text-danger text-sm">
          {add.error.message}
        </p>
      ) : null}
    </div>
  );
}

/**
 * The coach's one-line summary of an entry. Exported for the test that renders
 * it beside the client's `ExerciseCard` and asserts the two say the same thing.
 */
export function EntryLine({ entry }: { entry: ExerciseEntryRow }) {
  const detail = formatExerciseDetail(entry);

  return (
    <div>
      <span className="text-ink text-sm font-medium">{entry.name}</span>
      {detail === '' ? null : <span className="text-ink-soft text-sm"> · {detail}</span>}
      {entry.note === null ? null : (
        <span className="text-ink-muted ml-2 text-xs">{entry.note}</span>
      )}
    </div>
  );
}

/**
 * "Make it yours": her name and her cue on a stock exercise she has adopted.
 *
 * Her words sit **on top of** the catalogue's, never instead of them — the
 * stock description stays where it was, and the client is shown whose words are
 * whose. Nothing here edits the catalogue; it edits her own row, which is the
 * entire point of adoption (`exercise-catalogue`, "Her words sit on top, not
 * instead").
 */
function MakeItYours({
  option,
  onDone,
}: {
  option: PickerOption;
  onDone: (renamed: string | null) => void;
}) {
  const itemId = option.libraryItemId;
  const [name, setName] = useState(option.name);
  const [cue, setCue] = useState(option.cue ?? '');

  const save = useMutation({
    mutationFn: () => {
      if (itemId === null) throw new Error('that exercise is not yours to rename yet');
      return updateLibraryItem(supabase, itemId, {
        name: name.trim(),
        cue: cue.trim() === '' ? null : cue.trim(),
      });
    },
    onSuccess: (row) => {
      onDone(row.name);
    },
  });

  return (
    <div className="border-border bg-surface-muted flex flex-col gap-3 rounded-[--radius-control] border p-3">
      <p className="text-ink-muted text-xs">
        Your name and your cue for this exercise. Your clients see these; the general description
        stays underneath.
      </p>

      <div className="flex flex-wrap items-end gap-3">
        <Field
          label="Your name for it"
          className="min-w-40 flex-1"
          value={name}
          onChange={(event) => {
            setName(event.target.value);
          }}
        />
        <Field
          label="Your cue"
          className="min-w-48 flex-1"
          placeholder="Sit back, heels down."
          value={cue}
          onChange={(event) => {
            setCue(event.target.value);
          }}
        />
        <Button
          type="button"
          disabled={save.isPending || name.trim() === ''}
          onClick={() => {
            save.mutate();
          }}
        >
          Save
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            onDone(null);
          }}
        >
          Cancel
        </Button>
      </div>

      {save.isError ? (
        <p role="alert" className="text-danger text-sm">
          {save.error.message}
        </p>
      ) : null}
    </div>
  );
}
